import { CardElement } from "../types/battle"
import {
  ActiveTimeProgress,
  CosmeticKey,
  GuardianRankInfo,
  GuardianRankReward,
  PetActionResponse,
  PetActiveTimeResponse,
  PetCatalogEntry,
  PetDbRecord,
  PetFeedPortion,
  PetFeedState,
  PetPerk,
  PetPublicBrief,
  PetRenameState,
  PetScreenResponse,
  PetView,
} from "../types/pet"
import { normalizeCardElement } from "./cardElement"

type RawRecord = Record<string, unknown>

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const COSMETIC_KEYS: CosmeticKey[] = [
  "RANK_ICON_5",
  "PASSIVE_BOOST_RANK_8",
  "PET_AWAKEN",
  "PET_FORM_12",
  "DUST_BONUS_RANK_15",
  "COMBAT_CAP_RANK_20",
  "FRAME_XXV",
  "TITLE_VETERAN_30",
  "FRAME_II_35",
  "CARD_AURA_I_40",
  "CARD_AURA_II_50",
  "FRAME_III_60",
  "CARD_AURA_III_75",
  "FRAME_LEGEND_90",
  "AURA_ULTIMATE_100",
]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === "true" || value === 1) return true
  if (value === "false" || value === 0) return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const inner = obj.data ?? obj.payload ?? obj.result
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as RawRecord
  }
  return obj
}

function normalizeCosmeticKey(value: unknown): CosmeticKey | null {
  const raw = pickString(value).toUpperCase()
  return COSMETIC_KEYS.includes(raw as CosmeticKey) ? (raw as CosmeticKey) : null
}

function normalizePetPerk(raw: unknown): PetPerk | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const id = pickString(obj.id ?? obj.perkId ?? obj.perk_id)
  if (!id) return null
  return {
    id,
    title: pickString(obj.title ?? obj.name, id),
    description: pickString(obj.description ?? obj.desc),
  }
}

function normalizePetPerks(raw: unknown): PetPerk[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizePetPerk)
    .filter((p): p is PetPerk => p != null)
}

function normalizeGuardianRankReward(raw: unknown): GuardianRankReward | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const rank = pickNumber(obj.rank)
  if (!rank) return null
  const cosmeticKey = pickString(obj.cosmeticKey ?? obj.cosmetic_key)
  return {
    rank,
    hours: pickNumber(obj.hours),
    label: pickString(obj.label),
    cosmeticKey,
    combat: pickBool(obj.combat),
  }
}

export function normalizeGuardianRankInfo(raw: unknown): GuardianRankInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const rank = pickNumber(obj.rank, 1)
  const cosmeticsRaw =
    obj.unlockedCosmetics ??
    obj.unlocked_cosmetics ??
    obj.cosmeticUnlocked ??
    obj.cosmetic_unlocked
  const cosmetics = Array.isArray(cosmeticsRaw)
    ? cosmeticsRaw
        .map(normalizeCosmeticKey)
        .filter((k): k is CosmeticKey => k != null)
    : []

  const nextRewardRaw = obj.nextReward ?? obj.next_reward
  const nextReward = nextRewardRaw
    ? normalizeGuardianRankReward(nextRewardRaw)
    : null
  const nextRankAtHoursRaw = obj.nextRankAtHours ?? obj.next_rank_at_hours

  return {
    rank,
    maxRank: pickNumber(obj.maxRank ?? obj.max_rank, 100),
    maxCombatRank: pickNumber(obj.maxCombatRank ?? obj.max_combat_rank, 20),
    nextRank: pickNumber(obj.nextRank ?? obj.next_rank, rank + 1),
    progressPercent: pickNumber(obj.progressPercent ?? obj.progress_percent),
    activeHours: pickNumber(obj.activeHours ?? obj.active_hours),
    activeMinutes: pickNumber(obj.activeMinutes ?? obj.active_minutes),
    passiveAmp: pickNumber(obj.passiveAmp ?? obj.passive_amp),
    dustMult: pickNumber(obj.dustMult ?? obj.dust_mult, 1),
    unlockedCosmetics: cosmetics,
    nextReward,
    isCombatCapped: pickBool(obj.isCombatCapped ?? obj.is_combat_capped),
    nextRankAtHours:
      nextRankAtHoursRaw == null || nextRankAtHoursRaw === ""
        ? undefined
        : pickNumber(nextRankAtHoursRaw),
  }
}

function collectGuardianRankSources(raw: unknown): RawRecord[] {
  const sources: unknown[] = [raw]
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as RawRecord
    sources.push(
      obj.player,
      obj.passport,
      obj.profile,
      obj.user,
      obj.activeTime,
      obj.active_time
    )
  }

  const records: RawRecord[] = []
  for (const source of sources) {
    if (!source || typeof source !== "object" || Array.isArray(source)) continue
    records.push(source as RawRecord)
  }
  return records
}

export function pickGuardianRankInfoFromRecord(
  raw: unknown
): GuardianRankInfo | null {
  for (const obj of collectGuardianRankSources(raw)) {
    const fromInfo = normalizeGuardianRankInfo(
      obj.guardianRankInfo ?? obj.guardian_rank_info
    )
    if (fromInfo?.rank != null && fromInfo.rank > 0) return fromInfo

    const rankRaw = obj.guardianRank ?? obj.guardian_rank
    if (rankRaw && typeof rankRaw === "object" && !Array.isArray(rankRaw)) {
      const fromRankObject = normalizeGuardianRankInfo(rankRaw)
      if (fromRankObject?.rank != null && fromRankObject.rank > 0) {
        return fromRankObject
      }
    }
  }
  return null
}

export function pickGuardianRankFromRecord(raw: unknown): number | undefined {
  const info = pickGuardianRankInfoFromRecord(raw)
  if (info?.rank != null && info.rank > 0) return info.rank

  for (const obj of collectGuardianRankSources(raw)) {
    const activeTime = obj.activeTime ?? obj.active_time
    if (activeTime && typeof activeTime === "object" && !Array.isArray(activeTime)) {
      const at = activeTime as RawRecord
      const fromActive = pickNumber(at.guardianRank ?? at.guardian_rank, 0)
      if (fromActive > 0) return fromActive
    }

    const rankRaw = obj.guardianRank ?? obj.guardian_rank
    if (rankRaw != null && rankRaw !== "" && typeof rankRaw !== "object") {
      const n = pickNumber(rankRaw, 0)
      if (n > 0) return n
    }
  }

  return undefined
}

export function normalizeActiveTimeProgress(raw: unknown): ActiveTimeProgress | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  return {
    activeTimeTotalSec: pickNumber(
      obj.activeTimeTotalSec ?? obj.active_time_total_sec
    ),
    activeTimeTodaySec: pickNumber(
      obj.activeTimeTodaySec ?? obj.active_time_today_sec
    ),
    dailyCapSec: pickNumber(obj.dailyCapSec ?? obj.daily_cap_sec, 5400),
    dailyRemainingSec: pickNumber(
      obj.dailyRemainingSec ?? obj.daily_remaining_sec
    ),
    guardianRank: pickNumber(obj.guardianRank ?? obj.guardian_rank, 1),
    unlockRequiredSec: pickNumber(
      obj.unlockRequiredSec ?? obj.unlock_required_sec,
      54000
    ),
    canAwaken: pickBool(obj.canAwaken ?? obj.can_awaken),
    spiritAwakened: pickBool(obj.spiritAwakened ?? obj.spirit_awakened),
    progressToUnlockPercent: pickNumber(
      obj.progressToUnlockPercent ?? obj.progress_to_unlock_percent
    ),
  }
}

function normalizePetCatalogEntry(raw: unknown): PetCatalogEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = normalizeCardElement(obj.element)
  if (!element) return null
  return {
    element,
    defaultName: pickString(obj.defaultName ?? obj.default_name),
    perksLevel7: normalizePetPerks(obj.perksLevel7 ?? obj.perks_level_7),
    perksLevel12: normalizePetPerks(obj.perksLevel12 ?? obj.perks_level_12),
  }
}

export function normalizePetView(raw: unknown): PetView | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = normalizeCardElement(obj.element)
  if (!element) return null
  return {
    element,
    defaultName: pickString(obj.defaultName ?? obj.default_name),
    displayName: pickString(obj.displayName ?? obj.display_name),
    level: pickNumber(obj.level, 1),
    xp: pickNumber(obj.xp),
    battleXpTotal: pickNumber(obj.battleXpTotal ?? obj.battle_xp_total),
    formLabel: pickString(obj.formLabel ?? obj.form_label),
    maxLevel: pickNumber(obj.maxLevel ?? obj.max_level, 20),
    maxXp: pickNumber(obj.maxXp ?? obj.max_xp, 8000),
    perkLevel7:
      pickString(obj.perkLevel7 ?? obj.perk_level_7) || null,
    perkLevel12:
      pickString(obj.perkLevel12 ?? obj.perk_level_12) || null,
    perksLevel7: normalizePetPerks(obj.perksLevel7 ?? obj.perks_level_7),
    perksLevel12: normalizePetPerks(obj.perksLevel12 ?? obj.perks_level_12),
    lastRenameAt:
      pickString(obj.lastRenameAt ?? obj.last_rename_at) || null,
  }
}

export function normalizePetPublicBrief(raw: unknown): PetPublicBrief | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = normalizeCardElement(obj.element)
  if (!element) return null
  const defaultName = pickString(obj.defaultName ?? obj.default_name)
  const displayName =
    pickString(obj.displayName ?? obj.display_name) || defaultName
  if (!displayName && !defaultName) return null
  return {
    element,
    displayName: displayName || defaultName,
    defaultName,
    level: Math.max(1, pickNumber(obj.level, 1)),
    perkLevel7: pickString(obj.perkLevel7 ?? obj.perk_level_7) || null,
    perkLevel12: pickString(obj.perkLevel12 ?? obj.perk_level_12) || null,
    perkLevel7Title:
      pickString(obj.perkLevel7Title ?? obj.perk_level_7_title) || null,
    perkLevel12Title:
      pickString(obj.perkLevel12Title ?? obj.perk_level_12_title) || null,
  }
}

export function normalizePetDbRecord(raw: unknown): PetDbRecord | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = normalizeCardElement(obj.element)
  if (!element) return null
  return {
    element,
    defaultName: pickString(obj.defaultName ?? obj.default_name) || undefined,
    displayName: pickString(obj.displayName ?? obj.display_name),
    level: pickNumber(obj.level, 1),
    xp: pickNumber(obj.xp),
    battleXpTotal: pickNumber(obj.battleXpTotal ?? obj.battle_xp_total),
    perkLevel7:
      pickString(obj.perkLevel7 ?? obj.perk_level_7) || null,
    perkLevel12:
      pickString(obj.perkLevel12 ?? obj.perk_level_12) || null,
    lastRenameAt:
      pickString(obj.lastRenameAt ?? obj.last_rename_at) || null,
  }
}

function normalizePetRenameState(raw: unknown): PetRenameState {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  return {
    canRename: pickBool(obj.canRename ?? obj.can_rename, true),
    nextRenameAt:
      pickString(obj.nextRenameAt ?? obj.next_rename_at) || null,
    daysLeft: pickNumber(obj.daysLeft ?? obj.days_left),
  }
}

function normalizePetFeedPortion(raw: unknown): PetFeedPortion | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const portion = pickNumber(obj.portion)
  if (!portion) return null
  return {
    portion,
    dust: pickNumber(obj.dust),
    xp: pickNumber(obj.xp),
  }
}

function normalizePetFeedState(raw: unknown): PetFeedState {
  const obj =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as RawRecord)
      : {}
  const portionsRaw = Array.isArray(obj.portions) ? obj.portions : []
  const portions = portionsRaw
    .map(normalizePetFeedPortion)
    .filter((p): p is PetFeedPortion => p != null)

  return {
    portionsUsedToday: pickNumber(
      obj.portionsUsedToday ?? obj.portions_used_today
    ),
    maxPortionsPerDay: pickNumber(
      obj.maxPortionsPerDay ?? obj.max_portions_per_day,
      3
    ),
    feedXpToday: pickNumber(obj.feedXpToday ?? obj.feed_xp_today),
    feedXpDailyCap: pickNumber(obj.feedXpDailyCap ?? obj.feed_xp_daily_cap, 50),
    portions:
      portions.length > 0
        ? portions
        : [
            { portion: 1, dust: 40, xp: 25 },
            { portion: 2, dust: 80, xp: 50 },
            { portion: 3, dust: 120, xp: 75 },
          ],
  }
}

export function normalizePetScreenResponse(data: unknown): PetScreenResponse | null {
  const obj = unwrapPayload(data)
  const activeTime = normalizeActiveTimeProgress(
    obj.activeTime ?? obj.active_time
  )
  const guardianRank = normalizeGuardianRankInfo(
    obj.guardianRank ?? obj.guardian_rank
  )
  if (!activeTime || !guardianRank) return null

  const catalogRaw = Array.isArray(obj.catalog) ? obj.catalog : []
  const catalog = catalogRaw
    .map(normalizePetCatalogEntry)
    .filter((e): e is PetCatalogEntry => e != null)

  const affiliationRaw = pickString(
    obj.currentAffiliationElement ??
      obj.current_affiliation_element ??
      obj.affiliationElement ??
      obj.affiliation_element
  ).toUpperCase()
  const currentAffiliationElement = CARD_ELEMENTS.includes(
    affiliationRaw as CardElement
  )
    ? (affiliationRaw as CardElement)
    : null

  const petRaw = obj.pet
  const pet = petRaw != null ? normalizePetView(petRaw) : null

  return {
    catalog,
    activeTime,
    guardianRank,
    spiritAwakened: pickBool(
      obj.spiritAwakened ?? obj.spirit_awakened,
      activeTime.spiritAwakened
    ),
    canAwaken: pickBool(obj.canAwaken ?? obj.can_awaken, activeTime.canAwaken),
    currentAffiliationElement,
    preAwakenPassiveMult: pickNumber(
      obj.preAwakenPassiveMult ?? obj.pre_awaken_passive_mult,
      0.5
    ),
    pet,
    rename: normalizePetRenameState(obj.rename),
    feed: normalizePetFeedState(obj.feed),
  }
}

export function normalizePetActiveTimeResponse(
  data: unknown
): PetActiveTimeResponse {
  const obj = unwrapPayload(data)
  const hasPassport = pickBool(obj.hasPassport ?? obj.has_passport, true)
  if (!hasPassport) return { hasPassport: false }

  const activeTime = normalizeActiveTimeProgress(obj)
  const guardianRankInfo = normalizeGuardianRankInfo(
    obj.guardianRank ??
      obj.guardian_rank ??
      obj.guardianRankInfo ??
      obj.guardian_rank_info
  )

  if (activeTime) {
    return {
      hasPassport: true,
      ...activeTime,
      guardianRankInfo: guardianRankInfo ?? undefined,
    }
  }

  return {
    hasPassport: true,
    activeTimeTotalSec: pickNumber(
      obj.activeTimeTotalSec ?? obj.active_time_total_sec
    ),
    activeTimeTodaySec: pickNumber(
      obj.activeTimeTodaySec ?? obj.active_time_today_sec
    ),
    dailyCapSec: pickNumber(obj.dailyCapSec ?? obj.daily_cap_sec, 5400),
    dailyRemainingSec: pickNumber(
      obj.dailyRemainingSec ?? obj.daily_remaining_sec
    ),
    guardianRank: pickNumber(obj.guardianRank ?? obj.guardian_rank, 1),
    unlockRequiredSec: pickNumber(
      obj.unlockRequiredSec ?? obj.unlock_required_sec,
      54000
    ),
    canAwaken: pickBool(obj.canAwaken ?? obj.can_awaken),
    spiritAwakened: pickBool(obj.spiritAwakened ?? obj.spirit_awakened),
    progressToUnlockPercent: pickNumber(
      obj.progressToUnlockPercent ?? obj.progress_to_unlock_percent
    ),
    guardianRankInfo: guardianRankInfo ?? undefined,
  }
}

export function normalizePetActionResponse(data: unknown): PetActionResponse {
  const obj = unwrapPayload(data)
  const passportRaw = obj.passport
  let passport: PetActionResponse["passport"]
  if (passportRaw && typeof passportRaw === "object" && !Array.isArray(passportRaw)) {
    const p = passportRaw as RawRecord
    const element = normalizeCardElement(
      p.affiliationElement ?? p.affiliation_element
    )
    passport = {
      affiliationElement: element ?? undefined,
      spiritAwakened: pickBool(p.spiritAwakened ?? p.spirit_awakened)
        ? true
        : undefined,
    }
  }

  const pet = obj.pet != null ? normalizePetView(obj.pet) ?? undefined : undefined
  const rename =
    obj.rename != null ? normalizePetRenameState(obj.rename) : undefined
  const perkRaw = obj.perk
  const perk =
    perkRaw != null ? normalizePetPerk(perkRaw) ?? undefined : undefined

  return {
    pet,
    passport,
    rename,
    dustSpent: pickNumber(obj.dustSpent ?? obj.dust_spent) || undefined,
    xpGained: pickNumber(obj.xpGained ?? obj.xp_gained) || undefined,
    perk,
  }
}

export function normalizeUnlockedCosmetics(raw: unknown): CosmeticKey[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeCosmeticKey)
    .filter((k): k is CosmeticKey => k != null)
}
